import { useCallback, useMemo, useState } from 'react';
import { listarCategorias } from '../../api/cadastros-do-erp.js';
import type { CategoriaDoErp, FiltroDeCategorias } from '../../api/cadastros-do-erp.js';
import { contagemDoRecorte, intervaloDaPagina, totalDePaginas } from '../../api/paginacao.js';
import { seInformado } from '../../api/recorte.js';
import { useConsulta } from '../../api/useConsulta.js';
import { EstadoVazio } from '../../componentes/EstadoVazio.js';
import { FaixaDeFiltros } from '../../componentes/FaixaDeFiltros.js';
import { CampoBusca, ContadorRegistros, FiltroSelecao } from '../../componentes/Filtros.js';
import { IconeArmazem } from '../../componentes/Icones.js';
import { Paginacao } from '../../componentes/Paginacao.js';
import { Resultado } from '../../componentes/Resultado.js';
import { Tabela } from '../../componentes/Tabela.js';
import type { Coluna } from '../../componentes/Tabela.js';
import { useValorAdiado } from '../movimentacoes/useValorAdiado.js';
import { DatasDaSincronizacao, SituacaoNoErp } from './partes.js';
import {
  OPCOES_DE_SITUACAO_DA_CATEGORIA,
  SITUACAO_PADRAO_DA_CATEGORIA,
  SITUACOES_DA_CATEGORIA,
  valorDoSeletor,
} from './situacao.js';

const NUMERO = new Intl.NumberFormat('pt-BR');

const COLUNAS: readonly Coluna<CategoriaDoErp>[] = [
  {
    chave: 'codigo',
    rotulo: 'Código',
    largura: '14%',
    celula: (categoria) => <span className="sincronizacao__codigo">{categoria.codigo}</span>,
  },
  {
    chave: 'nome',
    rotulo: 'Nome',
    largura: '32%',
    celula: (categoria) => (
      <span className="sincronizacao__pilha">
        <span>{categoria.nome}</span>
        {categoria.descricao !== null && (
          <span className="sincronizacao__discreto">{categoria.descricao}</span>
        )}
      </span>
    ),
  },
  {
    chave: 'produtos',
    rotulo: 'Produtos ativos',
    largura: '120px',
    alinhamento: 'direita',
    celula: (categoria) => (
      <span className="quantidade">{NUMERO.format(categoria.quantidadeProdutos)}</span>
    ),
  },
  {
    chave: 'situacao',
    rotulo: 'Situação',
    largura: '170px',
    celula: (categoria) => (
      <SituacaoNoErp ativo={categoria.ativo} excluidoEm={categoria.excluidoEm} feminino />
    ),
  },
  {
    chave: 'datas',
    rotulo: 'Datas',
    celula: (categoria) => (
      <DatasDaSincronizacao
        atualizadoEm={categoria.atualizadoEm}
        sincronizadoEm={categoria.sincronizadoEm}
      />
    ),
  },
];

/** As categorias de produto como a sincronização as gravou do ERP. */
export function AbaCategorias() {
  const [busca, definirBusca] = useState('');
  const [situacao, definirSituacao] = useState<string>(SITUACAO_PADRAO_DA_CATEGORIA);
  const [pagina, definirPagina] = useState(1);
  const buscaAdiada = useValorAdiado(busca);

  const filtro = useMemo<FiltroDeCategorias>(
    () => ({
      pagina,
      ...seInformado('busca', buscaAdiada),
      ...seInformado('situacao', valorDoSeletor(situacao, SITUACOES_DA_CATEGORIA)),
    }),
    [pagina, buscaAdiada, situacao],
  );

  const buscar = useCallback(() => listarCategorias(filtro), [filtro]);
  const consulta = useConsulta(buscar);

  function recortar(aplicar: () => void) {
    aplicar();
    definirPagina(1);
  }

  return (
    <div className="sincronizacao__aba">
      <FaixaDeFiltros rotulo="Filtros das categorias">
        <CampoBusca
          rotulo="Buscar por código ou nome"
          valor={busca}
          largura={260}
          aoMudar={(valor) => {
            recortar(() => {
              definirBusca(valor);
            });
          }}
        />
        <FiltroSelecao
          rotulo="Situação"
          valor={situacao}
          opcoes={OPCOES_DE_SITUACAO_DA_CATEGORIA}
          aoMudar={(valor) => {
            recortar(() => {
              definirSituacao(valor);
            });
          }}
        />
        {consulta.dados !== null && (
          <ContadorRegistros>
            {contagemDoRecorte(consulta.dados.total, 'categoria', 'categorias', 'Nenhuma')}
          </ContadorRegistros>
        )}
      </FaixaDeFiltros>

      <Resultado
        consulta={consulta}
        espera="Carregando as categorias…"
        estaVazio={(dados) => dados.itens.length === 0}
        vazio={
          <EstadoVazio
            tom="neutro"
            icone={<IconeArmazem tamanho={22} />}
            titulo="Nenhuma categoria neste recorte."
            detalhe="As categorias chegam do ERP pela carga inicial e pela sincronização periódica."
          />
        }
      >
        {(dados) => (
          <Tabela
            itens={dados.itens}
            colunas={COLUNAS}
            chaveDe={(categoria) => categoria.codigo}
            descricao="Categorias do ERP"
            rodape={
              <Paginacao
                intervalo={intervaloDaPagina(dados, 'categoria', 'categorias')}
                pagina={dados.pagina}
                totalPaginas={totalDePaginas(dados)}
                aoIrPara={definirPagina}
              />
            }
          />
        )}
      </Resultado>
    </div>
  );
}
